// Strip GPS EXIF from a JPEG, keep everything else (orientation, timestamp).
// CLAUDE.md hard requirement.
//
// JPEG structure: SOI (0xFFD8) then a sequence of segments (0xFF <marker> <2-byte len>).
// EXIF lives in an APP1 segment (0xFFE1) whose payload starts with "Exif\0\0".
// GPS IFD is at TIFF offset TAG 0x8825 inside the APP1 payload.
//
// Approach: locate APP1/Exif, walk to the GPS IFD pointer in IFD0, then either
// zero the pointer or blank the referenced GPS IFD. We do the safer thing:
// leave IFD0 intact, but overwrite the GPS IFD entry count to 0 and null the
// GPS entries so downstream parsers see "no GPS".
//
// If parsing fails at any step we fall back to returning the original buffer
// unchanged. A wedding photo without GPS-strip is a worse leak than a slightly
// slower upload — this must not throw.

export function stripGpsExif(buf: ArrayBuffer): ArrayBuffer {
  try {
    const view = new DataView(buf);
    if (view.getUint16(0) !== 0xffd8) return buf; // not a JPEG

    let offset = 2;
    while (offset < view.byteLength) {
      const marker = view.getUint16(offset);
      if ((marker & 0xff00) !== 0xff00) break;
      const size = view.getUint16(offset + 2);
      if (marker === 0xffe1) {
        // APP1 — check for "Exif"
        const magic = String.fromCharCode(
          view.getUint8(offset + 4),
          view.getUint8(offset + 5),
          view.getUint8(offset + 6),
          view.getUint8(offset + 7)
        );
        if (magic === 'Exif') {
          const tiffStart = offset + 10; // after "Exif\0\0"
          const little = view.getUint16(tiffStart) === 0x4949;
          const ifd0Offset = readUint32(view, tiffStart + 4, little);
          const ifd0Ptr = tiffStart + ifd0Offset;
          const numEntries = readUint16(view, ifd0Ptr, little);
          for (let i = 0; i < numEntries; i++) {
            const entry = ifd0Ptr + 2 + i * 12;
            const tag = readUint16(view, entry, little);
            if (tag === 0x8825) {
              // Point GPS IFD entry to itself with count 0 → effectively empty.
              // Simpler: overwrite the tag so nobody looks at it.
              writeUint16(view, entry, 0xa500, little); // repurposed as non-GPS tag
              // Zero the "value" (offset to GPS IFD) — 4 bytes at entry+8
              view.setUint32(entry + 8, 0, little);
              break;
            }
          }
        }
      }
      offset += 2 + size;
    }
    return buf;
  } catch {
    return buf;
  }
}

function readUint16(v: DataView, o: number, little: boolean): number {
  return v.getUint16(o, little);
}
function readUint32(v: DataView, o: number, little: boolean): number {
  return v.getUint32(o, little);
}
function writeUint16(v: DataView, o: number, val: number, little: boolean) {
  v.setUint16(o, val, little);
}
